import { createInterfaceId, Implements, Service, ServiceLifetime } from '@gitlab/needle';
import { Logger, withPrefix } from '@gitlab-org/logging';
import { SecretRedactor } from '@gitlab-org/secret-redaction';
import {
  CLI_INPUT_TYPES,
  type AppState,
  type ApprovalScope,
  type ChoiceOption,
  type ToolCall,
} from '@gitlab-org/tui';
import { DuoAgentPlatformEvent, DuoAgentPlatformTracker } from '@gitlab-org/telemetry';
import { ApprovalSource } from '@gitlab-lsp/workflow-api';
import { type AgentMode, type ToolApprovalAction, UserActionType } from '../../backend/backend';
import type { ControllerApi } from './controller_api';

const MAX_REJECTION_REASON_LENGTH = 500;

export interface ToolApprovalCallbacks {
  onSubmitRejectionReason: (reason: string) => void;
  onCancelRejectionReason: () => void;
}

export interface ApprovalRequest {
  toolCall: ToolCall;
  /** Sub-agent name shown in the prompt; omitted for the root or when unknown. */
  agentLabel?: string;
}

export interface ToolApprovalHandler {
  presentApprovalChoices(
    api: ControllerApi,
    requests: ApprovalRequest[],
    agentMode?: AgentMode,
  ): void;
  handleChoiceSubmit(api: ControllerApi, action: ToolApprovalAction): void;
  getCallbacks(api: ControllerApi): ToolApprovalCallbacks;
  isPromptingFor(state: AppState, toolId: string): boolean;
}

export const ToolApprovalHandler = createInterfaceId<ToolApprovalHandler>('ToolApprovalHandler');

@Implements(ToolApprovalHandler)
@Service({
  dependencies: [Logger, SecretRedactor, DuoAgentPlatformTracker],
  lifetime: ServiceLifetime.Singleton,
})
export class DefaultToolApprovalHandler implements ToolApprovalHandler {
  #logger: Logger;

  #secretRedactor: SecretRedactor;

  #tracker: DuoAgentPlatformTracker;

  #lastAvailableScopes: ApprovalScope[] = [];

  /** The request currently being prompted for, for the rejection round trip. */
  #currentRequest: ApprovalRequest | undefined;

  #pendingRejection:
    | {
        toolId: string;
        toolName: string;
        agentMode?: AgentMode;
        toolArgs?: Record<string, unknown>;
        availableScopes: ApprovalScope[];
        agentLabel?: string;
        toolCall?: ToolCall;
      }
    | undefined;

  #approvalQueue: { request: ApprovalRequest; agentMode?: AgentMode }[] = [];

  constructor(logger: Logger, secretRedactor: SecretRedactor, tracker: DuoAgentPlatformTracker) {
    this.#logger = withPrefix(logger, '[ToolApprovalHandler]');
    this.#secretRedactor = secretRedactor;
    this.#tracker = tracker;
  }

  presentApprovalChoices(
    api: ControllerApi,
    requests: ApprovalRequest[],
    agentMode?: AgentMode,
  ): void {
    if (requests.length === 0) return;

    this.#pendingRejection = undefined;
    this.#approvalQueue = [];

    // Queue all tools after the first
    for (const request of requests.slice(1)) {
      this.#approvalQueue.push({ request, agentMode });
    }

    this.#presentSingleApproval(api, requests[0], agentMode);
  }

  #presentSingleApproval(
    api: ControllerApi,
    request: ApprovalRequest,
    agentMode?: AgentMode,
  ): void {
    const { toolCall } = request;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { tool: _tool, ...toolArgs } = toolCall.input || {};

    // Extract available scopes from toolCall state, default to 'once' only (conservative)
    const availableScopes: ApprovalScope[] =
      toolCall.state.type === 'approval_request' ? toolCall.state.availableScopes : ['once'];

    this.#lastAvailableScopes = availableScopes;
    this.#currentRequest = request;

    this.#setChoiceState(
      api,
      toolCall.id,
      toolCall.name,
      agentMode,
      toolArgs,
      availableScopes,
      DefaultToolApprovalHandler.#approvalBlock(request.agentLabel, toolCall),
    );
  }

  /** The self-contained block the prompt renders when the pending card is out of view. */
  static #approvalBlock(
    agentLabel: string | undefined,
    toolCall: ToolCall | undefined,
  ): { toolCall: ToolCall; requestedBy?: string } | undefined {
    if (!toolCall) return undefined;
    return { toolCall, ...(agentLabel ? { requestedBy: agentLabel } : {}) };
  }

  handleChoiceSubmit(api: ControllerApi, action: ToolApprovalAction): void {
    this.#logger.debug(
      `handleChoiceSubmit: tool=${action.toolName} approved=${action.approved} approvalSource=${action.approvalSource ?? '(none)'}`,
    );

    if (action.approved) {
      this.#tracker.trackEvent(DuoAgentPlatformEvent.ToolApprovalSubmitted, {
        source: 'cli',
        toolName: action.toolName,
        approvalScope: action.scope,
      });
      api.sendToolApproval(action);
      this.#drainQueueWithApproval(api, action);
      return;
    }

    this.#pendingRejection = {
      toolId: action.toolId,
      toolName: action.toolName,
      agentMode: action.agentMode,
      toolArgs: action.toolArgs,
      availableScopes: this.#lastAvailableScopes,
      agentLabel: this.#currentRequest?.agentLabel,
      toolCall: this.#currentRequest?.toolCall,
    };
    this.#logger.debug(`Requesting rejection reason for tool: ${action.toolName}`);

    api.mutateState((state) => ({
      ...state,
      input: {
        inputType: CLI_INPUT_TYPES.TOOL_REJECTION_REASON,
        toolName: action.toolName,
      },
    }));
  }

  /**
   * Applies the decision just submitted to the rest of the queued batch — the
   * user answered once for the parallel batch. The workflow pauses as soon as
   * any agent requests approval, so a batch is always a single agent's.
   */
  #drainQueueWithApproval(api: ControllerApi, action: ToolApprovalAction): void {
    const batch = this.#approvalQueue;
    this.#approvalQueue = [];

    for (const next of batch) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { tool: _tool, ...toolArgs } = next.request.toolCall.input || {};
      if (action.approved) {
        this.#tracker.trackEvent(DuoAgentPlatformEvent.ToolApprovalSubmitted, {
          source: 'cli',
          toolName: next.request.toolCall.name,
          approvalScope: action.scope,
        });
        api.sendToolApproval({
          type: UserActionType.SendToolApproval,
          toolId: next.request.toolCall.id,
          toolName: next.request.toolCall.name,
          agentMode: next.agentMode,
          toolArgs,
          approved: true,
          scope: action.scope,
          approvalSource: action.approvalSource,
        });
      } else {
        api.sendToolApproval({
          type: UserActionType.SendToolApproval,
          toolId: next.request.toolCall.id,
          toolName: next.request.toolCall.name,
          agentMode: next.agentMode,
          toolArgs,
          approved: false,
          rejectionReason: action.rejectionReason,
          approvalSource: action.approvalSource,
        });
      }
    }
  }

  getCallbacks(api: ControllerApi): ToolApprovalCallbacks {
    return {
      onSubmitRejectionReason: (reason: string) => {
        const pending = this.#pendingRejection;
        if (!pending) {
          this.#logger.warn('onSubmitRejectionReason called without pending rejection');
          return;
        }

        const redactedReason = this.#secretRedactor.redactSecrets(reason, 'user-input');
        const trimmedReason =
          redactedReason.trim().slice(0, MAX_REJECTION_REASON_LENGTH) || undefined;
        this.#pendingRejection = undefined;

        this.#logger.debug(
          `Rejecting tool ${pending.toolName} (${pending.toolId})${trimmedReason ? ' with reason' : ''}`,
        );

        // approvalSource on rejections is action-level provenance only (local debug
        // logs); it is dropped before reaching the backend. See ToolApprovalRejected
        // docs in workflow_message_types.ts.
        const rejectionAction: ToolApprovalAction = {
          type: UserActionType.SendToolApproval,
          toolId: pending.toolId,
          toolName: pending.toolName,
          agentMode: pending.agentMode,
          toolArgs: pending.toolArgs,
          approved: false,
          rejectionReason: trimmedReason,
          approvalSource: ApprovalSource.UserExplicit,
        };
        api.sendToolApproval(rejectionAction);
        this.#drainQueueWithApproval(api, rejectionAction);
      },

      onCancelRejectionReason: () => {
        const pending = this.#pendingRejection;
        if (!pending) {
          this.#logger.warn('onCancelRejectionReason called without pending rejection');
          return;
        }

        this.#logger.debug('Cancelled rejection reason input, returning to choice');
        this.#pendingRejection = undefined;
        if (pending.toolCall) {
          this.#currentRequest = {
            toolCall: pending.toolCall,
            agentLabel: pending.agentLabel,
          };
        }
        this.#setChoiceState(
          api,
          pending.toolId,
          pending.toolName,
          pending.agentMode,
          pending.toolArgs,
          pending.availableScopes,
          DefaultToolApprovalHandler.#approvalBlock(pending.agentLabel, pending.toolCall),
        );
      },
    };
  }

  isPromptingFor(state: AppState, toolId: string): boolean {
    if (state.input.inputType !== CLI_INPUT_TYPES.CHOICE) return false;
    const firstOption = state.input.choiceOptions[0]?.value as ToolApprovalAction | undefined;
    return firstOption?.toolId === toolId;
  }

  #setChoiceState(
    api: ControllerApi,
    toolId: string,
    toolName: string,
    agentMode?: AgentMode,
    toolArgs?: Record<string, unknown>,
    availableScopes?: ApprovalScope[],
    approval?: { toolCall: ToolCall; requestedBy?: string },
  ): void {
    const message = {
      type: UserActionType.SendToolApproval,
      toolId,
      toolName,
      agentMode,
      toolArgs,
      approvalSource: ApprovalSource.UserExplicit,
    } as const;

    const choiceOptions: ChoiceOption<ToolApprovalAction>[] = [
      { label: 'Approve', value: { ...message, approved: true, scope: 'once' } },
    ];

    // Simply check if 'session' is in available scopes - no capability logic needed!
    if (availableScopes?.includes('session')) {
      choiceOptions.push({
        label: 'Approve for session',
        value: { ...message, approved: true, scope: 'session' },
      });
    }

    choiceOptions.push({
      label: 'Reject',
      value: { ...message, approved: false },
    });

    api.mutateState((state) => ({
      ...state,
      input: {
        inputType: CLI_INPUT_TYPES.CHOICE,
        choiceOptions,
        selectedChoiceIndex: 0,
        ...(approval ? { approval } : {}),
      },
    }));
  }
}
